import { IQChatAddon, IQChatAttachment } from '../classes/iqBoost';
import type { LangGraphUpload } from '../services/langgraph-upload';

export const LANGGRAPH_ASSISTANT_IDS = ['anonymized_mcp'] as const;
export type LangGraphAssistantId = (typeof LANGGRAPH_ASSISTANT_IDS)[number];

export type LangGraphMessageRole = 'human' | 'ai' | 'system' | 'tool';

// Kept on the question for the chat to show when the thread is reopened. The server never puts
// `response_metadata` in a model request.
export interface LangGraphQuestionMetadata {
  attachments?: IQChatAttachment[];
  // Kept only when the content sent says more than the reader saw.
  question?: string;
  addon?: IQChatAddon;
}

export type LangGraphThreadSource = 'iqboost-all-households' | 'iqboost-single-household';

export interface LangGraphThreadScope {
  source: LangGraphThreadSource;
  entry_type?: string;
  entry_key?: string;
}

export const ALL_HOUSEHOLDS_THREAD_SCOPE: LangGraphThreadScope = {
  source: 'iqboost-all-households',
};

// The owner fields only keep one user's list apart from another's here; isolating threads for
// real is up to the server's auth.
export interface LangGraphThreadMetadata extends LangGraphThreadScope {
  owner_id: string;
  database_id: string;
  title?: string;
  pinned?: boolean;
  pinned_at?: string;
  [key: string]: unknown;
}

export interface LangGraphThread {
  thread_id: string;
  created_at: string;
  updated_at: string;
  // Unlike `updated_at`, a rename or a pin leaves it alone. The JS dev server does not report it.
  state_updated_at?: string | null;
  status: string;
  metadata: Record<string, unknown>;
  [key: string]: unknown;
}

export interface LangGraphRunOptions {
  uploads?: LangGraphUpload[];
  attachments?: IQChatAttachment[];
  addon?: IQChatAddon;
  question?: string;
  // An AI Redirect model id forced on this run; without it the orchestrator's default applies.
  model?: string;
  assistantId?: LangGraphAssistantId;
}

export interface LangGraphContentBlock {
  type: string;
  text?: string;
}

export interface LangGraphOutputMessage {
  id?: string;
  type: LangGraphMessageRole;
  name?: string | null;
  content: string | LangGraphContentBlock[];
  tool_calls?: unknown[];
  response_metadata?: Record<string, unknown>;
}

export interface LangGraphToolCallRef {
  id: string | null;
  name: string;
}

// A payload the orchestrator hands the chat beside its answer: `type` says how to read `data`.
export interface LangGraphHandoff {
  id: string;
  type: string;
  version: number;
  // The question whose answer it belongs with.
  questionId: string | null;
  createdAt: string;
  data: Record<string, unknown>;
}

export const WRITE_CONFIRMATION_TYPE = 'write_confirmation';

export type LangGraphConfirmationStatus = 'pending' | 'confirmed' | 'cancelled';

// `args` are sent to the tool as they are, real values included.
export interface LangGraphWriteAction {
  toolCallId: string;
  tool: string;
  args: Record<string, unknown>;
}

// The orchestrator stops before a tool that changes Maximizer data until the reader answers.
export interface LangGraphConfirmation {
  id: string;
  type: typeof WRITE_CONFIRMATION_TYPE;
  version: number;
  // The question whose answer shows it: one without is not read, since no answer could show it.
  questionId: string;
  data: { status: LangGraphConfirmationStatus; actions: LangGraphWriteAction[] };
}

export interface LangGraphConfirmationAnswer {
  id: string;
  approved: boolean;
}

export interface LangGraphConversation {
  messages: LangGraphOutputMessage[];
  handoffs: LangGraphHandoff[];
  // The decided ones, then the one the thread waits on, if any.
  confirmations: LangGraphConfirmation[];
}

// `anonymized_mcp` reports a model turn's tool calls again once it restored the turn, under a new
// message id but with the same call ids: that is how a repeated call is told apart.
export type LangGraphStreamUpdate =
  | { kind: 'text'; messageId: string; text: string }
  | { kind: 'toolCall'; tools: LangGraphToolCallRef[] }
  | { kind: 'toolResult'; toolName: string; toolCallId: string | null }
  | { kind: 'handoff'; handoff: LangGraphHandoff }
  | { kind: 'confirmation'; confirmation: LangGraphConfirmation }
  | { kind: 'end' };
